import {
  BackgroundCheckLocationMode,
  IBackgroundCheckPackageTemplate,
  IExperienceTemplateResource,
  ISubTaskBackgroundCheckInviteTemplate,
  StorageContext,
  SubTaskDataDestination,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { StandardUserField } from '@dadohr/shared/domainTypes/user';
import { CheckrReportDestinationSource } from '@dadohr/shared/domainTypes/checkr';
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { SetStateAction } from 'react';
import shortid from 'shortid';
import { AutocompleteSingle } from '../../../../../../../../components/Autocomplete';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../../components/SelectCustom';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../../../layout/Flex';
import {
  toSearchTermRegexp,
  useUserFields
} from '../../../../../../../../services/user';
import {
  CheckrNode,
  CheckrPackage,
  useCheckrPackages
} from '../../../../../../../Org/pages/Integrations/integrations/checkr/service';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_BACKGROUND_CHECK
} from '../../../validation';
import { buildScreeningSources } from './backgroundCheckSources';
import { StorageDestinations } from './dataDestinations';
// Re-exported for consumers that import the Checkr shapes from this module
// rather than from the integration service.
export type {
  CheckrNode,
  CheckrPackage,
  CheckrScreening
} from '@dadohr/shared/domainTypes/checkr';

// Which user-profile fields may be offered as the work location, for
// `IBackgroundCheckPackageTemplate.workLocationSourceField`.
//
// The same set in both location modes: Checkr wants a two-letter US state on a
// US invitation and a country on an international one, and the backend resolves
// whichever the mode calls for from whatever the customer keeps on the
// participant's profile.
const WORK_LOCATION_FIELD_TYPES: CustomUserField['type'][] = [
  'string',
  'address',
  'smartText'
];

// This component configures the background check invite sub task once added
// to a task. The picked package + node are stored on the subtask's `data`
// (for the validator) AND mirrored into a matching
// `IBackgroundCheckPackageTemplate` resource in the template's `resources`
// array (for the Resources section in TemplateMetaGrid). The two stores are
// kept in sync by the picker handlers.
export const BlockSettingsBackgroundCheckInvite = ({
  value,
  onChange,
  expTpl,
  resources,
  onChangeResources,
  validation,
  setValidation,
  customFields,
  goToSubPage,
  tokens
}: {
  value: ISubTaskBackgroundCheckInviteTemplate;
  onChange: (nextValue: ISubTaskBackgroundCheckInviteTemplate) => void;
  actor: string;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();

  // Live revalidation of the data destinations, mirroring the QUESTION subtask
  // (Question.tsx). Recomputes this block's `storage` error on every edit so
  // inline messages clear/appear immediately instead of only on the next save.
  const validateStorages = useFieldValidator(
    setValidation,
    VALIDATORS_BACKGROUND_CHECK,
    'storage'
  );

  const { packages, nodes, loading, error, refresh } = useCheckrPackages(
    expTpl.orgId
  );

  // --- Selection state lives directly on the subtask ---
  const selectedPackage = value.data.package;
  const selectedNode = value.data.node;
  const selectedWorkLocationField = value.data.workLocationSourceField ?? '';
  // Absent means US — every subtask authored before international support
  // predates the field and must keep behaving as a US check. New subtasks
  // write US explicitly (TaskBuilder `emptyBlock`), so the fallback only ever
  // covers pre-existing ones.
  const selectedLocationMode =
    value.data.locationMode ?? BackgroundCheckLocationMode.US;
  const isInternational =
    selectedLocationMode === BackgroundCheckLocationMode.INTERNATIONAL;

  const workLocationFieldOptions = useUserFields(customFields).filter((f) =>
    WORK_LOCATION_FIELD_TYPES.includes(f.type)
  );

  // --- Validation ---
  // linkText / package presence come from VALIDATORS_BACKGROUND_CHECK at
  // save & deselect. The package-in-node check below recomputes locally on
  // every render so inline feedback is immediate; the same static validator
  // gates save.
  // Helper-text errors only render while the field is still empty — once the
  // user has supplied a value the stale "required" message disappears even
  // though `validation.fields` still carries it (the framework state stays
  // intact for the next save).
  const linkTextError =
    !value.data.linkText.trim() && validation?.fields.linkText
      ? validation.fields.linkText
      : null;
  const packageRequiredError =
    !selectedPackage && validation?.fields.package
      ? validation.fields.package
      : null;
  // Gated the same way as linkTextError / packageRequiredError — only while
  // the field is still empty — but surfaced differently: the helper text
  // stays put and the field itself goes red, so the admin keeps the
  // explanation of what to pick instead of losing it to an error message.
  // Opening the task runs validateBlocks over every block, so an existing
  // (pre-SC-29734) subtask — which has no selection, and whose every invite
  // fails with WORK_LOCATION_FIELD_NOT_CONFIGURED — reddens on open.
  const workLocationFieldMissing =
    !selectedWorkLocationField.trim() &&
    !!validation?.fields.workLocationSourceField;
  const nodePackageError = validatePackageSelection(
    selectedPackage?.slug,
    selectedNode?.id,
    packages,
    nodes
  );

  // --- Handlers ---
  // Writes the selection to both the subtask `data` (validator source) and a
  // matching BG-check resource in `resources` (the copy the backend reads).
  // The resource is created on the first pick that includes a package, and
  // updated on subsequent picks. A node-only or work-location-only state
  // doesn't materialize a resource — only after a package gets picked, since
  // the resource's `package` is required. Those picks still persist on the
  // subtask mirror, so nothing is lost while the resource is deferred.
  const writeSelection = (next: {
    package?: CheckrPackage;
    node?: CheckrNode;
    workLocationSourceField?: string;
    locationMode?: BackgroundCheckLocationMode;
  }) => {
    const mergedData = { ...value.data, ...next };
    if (!mergedData.package) {
      onChange({ ...value, data: mergedData });
      return;
    }
    const existing = value.data.resourceId
      ? (resources.find(
          (r) =>
            r.type === TemplateResourceType.BACKGROUND_CHECK_PACKAGE &&
            r.resourceId === value.data.resourceId
        ) as IBackgroundCheckPackageTemplate | undefined)
      : undefined;
    if (existing) {
      const updated: IBackgroundCheckPackageTemplate = {
        ...existing,
        package: mergedData.package,
        node: mergedData.node,
        workLocationSourceField: mergedData.workLocationSourceField ?? '',
        locationMode: mergedData.locationMode ?? BackgroundCheckLocationMode.US
      };
      onChangeResources(
        resources.map((r) => (r === existing ? updated : r))
      );
      onChange({ ...value, data: mergedData });
      return;
    }
    const created: IBackgroundCheckPackageTemplate = {
      resourceId: shortid(),
      status: 'COMPLETE',
      type: TemplateResourceType.BACKGROUND_CHECK_PACKAGE,
      provider: 'CHECKR',
      package: mergedData.package,
      node: mergedData.node,
      workLocationSourceField: mergedData.workLocationSourceField ?? '',
      locationMode: mergedData.locationMode ?? BackgroundCheckLocationMode.US
    };
    onChangeResources([...resources, created]);
    onChange({
      ...value,
      data: { ...mergedData, resourceId: created.resourceId }
    });
  };

  // Changing the node clears a previously selected package that no longer
  // belongs to the new node, since the package picker now only offers
  // packages from the selected node. A node with an empty `packageSlugs`
  // list is treated as "no restriction" — any package is valid, so we don't
  // clear the existing selection.
  const onSelectNode = (node: CheckrNode | null) => {
    const next: { node?: CheckrNode; package?: CheckrPackage } = {
      node: node ?? undefined
    };
    const newNodeAllowsAnyPackage = !!node && node.packageSlugs.length === 0;
    if (
      selectedPackage &&
      !newNodeAllowsAnyPackage &&
      (!node || !node.packageSlugs.includes(selectedPackage.slug))
    ) {
      next.package = undefined;
    }
    writeSelection(next);
  };

  const onSelectPackage = (pkg: CheckrPackage | null) => {
    if (!pkg) return;
    writeSelection({ package: pkg });
  };

  // Package options are restricted to those the selected node has. When no
  // node is selected (and nodes exist) the picker is disabled, so the empty
  // option list is never user-facing. Two fallbacks show all packages:
  //   - The org has no nodes at all.
  //   - The selected node has an empty `packageSlugs` list — treated as
  //     "no restriction" rather than "no packages allowed", so the user
  //     isn't locked out by an unconfigured node.
  const selectedNodeAllowsAnyPackage =
    !!selectedNode && selectedNode.packageSlugs.length === 0;
  const filteredPackages =
    nodes.length === 0
      ? packages
      : selectedNode
      ? selectedNodeAllowsAnyPackage
        ? packages
        : packages.filter((p) => selectedNode.packageSlugs.includes(p.slug))
      : [];
  const packagePickerDisabled = nodes.length > 0 && !selectedNode;

  const onChangeLinkText = (linkText: string) =>
    onChange({ ...value, data: { ...value.data, linkText } });

  // --- Render ---
  return (
    <FlexContainerVertical fullWidth spacing={3}>
      <TextField
        label={t('subTask.backgroundCheck.invite.linkText.label')}
        value={value.data.linkText}
        fullWidth
        variant="outlined"
        error={!!linkTextError}
        helperText={
          linkTextError || t('subTask.backgroundCheck.invite.linkText.helper')
        }
        onChange={(ev) => onChangeLinkText(ev.target.value)}
      />

      <FlexContainerVertical fullWidth spacing={1}>
        <Typography variant="h6">{t('Package details')}</Typography>

        {loading && (
          <SelectCustom
            fullWidth
            label={t('subTask.backgroundCheck.invite.package.label')}
            loading
          />
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refresh()}>
                {t('subTask.backgroundCheck.invite.retry')}
              </Button>
            }
          >
            {t('subTask.backgroundCheck.invite.packages.loadError')}
          </Alert>
        )}

        {!loading && !error && packages.length === 0 && (
          <Typography>
            {t('subTask.backgroundCheck.invite.packages.empty')}
          </Typography>
        )}

        {!loading && !error && packages.length > 0 && (
          <FlexContainerVertical fullWidth spacing={2}>
            {nodes.length > 0 && (
              <CheckrNodePicker
                label={t('subTask.backgroundCheck.invite.node.label')}
                selectedNodeId={selectedNode?.id}
                onChange={onSelectNode}
                options={nodes}
              />
            )}
            <CheckrPackagePicker
              label={t('subTask.backgroundCheck.invite.package.label')}
              selectedPackageSlug={selectedPackage?.slug}
              fallbackName={selectedPackage?.name}
              onChange={onSelectPackage}
              options={filteredPackages}
              disabled={packagePickerDisabled}
              error={!!packageRequiredError || !!nodePackageError}
              helperText={
                packageRequiredError ||
                (nodePackageError && t(nodePackageError)) ||
                (packagePickerDisabled
                  ? t('subTask.backgroundCheck.invite.package.selectNodeFirst')
                  : null)
              }
            />
          </FlexContainerVertical>
        )}
      </FlexContainerVertical>

      <FlexContainerVertical fullWidth spacing={1}>
        <Typography variant="h6">{t('Location information')}</Typography>
        <LocationModePicker
          value={selectedLocationMode}
          onChange={(locationMode) => writeSelection({ locationMode })}
        />
        <WorkLocationFieldPicker
          label={
            isInternational
              ? t('Country (working location)')
              : t('State (working location)')
          }
          selectedKey={selectedWorkLocationField}
          options={workLocationFieldOptions}
          onChange={(field) =>
            writeSelection({ workLocationSourceField: field?.key || '' })
          }
          error={workLocationFieldMissing}
          helperText={
            isInternational
              ? t(
                  "Select a Dado user profile field that indicates the country where they'll work."
                )
              : t(
                  "Select a Dado user profile field that indicates the US state where they'll work."
                )
          }
        />
      </FlexContainerVertical>

      {selectedPackage && (
        <FlexContainerVertical fullWidth spacing={1}>
          <Typography variant="h6">
            {t('subTask.backgroundCheck.invite.screening.heading')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('subTask.backgroundCheck.invite.screening.description')}
          </Typography>
          <StorageDestinations
            value={value.data.storages || []}
            onChange={(nextStorages: SubTaskDataDestination[]) => {
              const nextValue = {
                ...value,
                data: { ...value.data, storages: nextStorages }
              };
              validateStorages(nextValue, expTpl);
              onChange(nextValue);
            }}
            resources={resources}
            onChangeResources={onChangeResources}
            expTpl={expTpl}
            customFields={customFields}
            goToSubPage={goToSubPage}
            tokens={tokens}
            validation={validation}
            storageContext={StorageContext.SUBTASK}
            isCompletionHookDataDestination={false}
            disallowUserProfile={false}
            sources={[
              ...[
                CheckrReportDestinationSource.REPORT_STATUS,
                CheckrReportDestinationSource.REPORT_RESULT,
                // SC-31016 — Adjudication source. Requires @dadohr/shared
                // to be republished with the REPORT_ADJUDICATION enum value
                // added in new-main/packages/shared/.../checkr.ts.
                CheckrReportDestinationSource.REPORT_ADJUDICATION
              ],
              ...buildScreeningSources(selectedPackage)
            ]}
            sourcesLabel={t('subTask.backgroundCheck.invite.screening.result')}
          />
        </FlexContainerVertical>
      )}
    </FlexContainerVertical>
  );
};

export const BlockSettingsBackgroundCheckInviteReadOnly = ({
  value
}: {
  value: ISubTaskBackgroundCheckInviteTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  return null;
};

/**
 * Picks whether this subtask orders a US or a non-US check. The value is the
 * admin's call rather than something derived from the package: a Checkr package
 * holds either US-only or international-only screenings, and the two have to be
 * matched by hand.
 */
const LocationModePicker = ({
  value,
  onChange
}: {
  value: BackgroundCheckLocationMode;
  onChange: (next: BackgroundCheckLocationMode) => void;
}) => {
  const { t } = useUnsafeTranslation();
  // Deliberately not a `SelectCustom`: this select carries no label, and
  // SelectCustom always renders an `InputLabel`, which with a value selected
  // shrinks into the outline and paints its paper background there — an empty
  // label leaves a notch-shaped gap in the border. The "Location information"
  // heading above names the group instead, so the accessible name is set by
  // hand rather than by a visible label.
  return (
    <FormControl fullWidth>
      <Select
        variant="outlined"
        value={value}
        onChange={(ev) =>
          onChange(ev.target.value as BackgroundCheckLocationMode)
        }
        inputProps={{ 'aria-label': t('Location information') }}
      >
        <MenuItem value={BackgroundCheckLocationMode.US}>{t('US')}</MenuItem>
        <MenuItem value={BackgroundCheckLocationMode.INTERNATIONAL}>
          {t('International')}
        </MenuItem>
      </Select>
    </FormControl>
  );
};

type WorkLocationFieldOption = StandardUserField | CustomUserField;

/**
 * Picks which user-profile field the backend should read the participant's
 * work location from. Emits a bare key for standard fields (`address`) and
 * the canonical prefixed form for custom ones (`customFields.workLocation`) —
 * `useUserFields` has already applied `toCustomFieldKey`, so the option's own
 * `key` is the value to store.
 */
const WorkLocationFieldPicker = ({
  selectedKey,
  onChange,
  options,
  label,
  error,
  helperText
}: {
  selectedKey: string;
  onChange: (next: WorkLocationFieldOption | null) => void;
  options: WorkLocationFieldOption[];
  label?: React.ReactNode;
  error?: boolean;
  helperText?: React.ReactNode;
}) => {
  // A previously-picked field that has since been deleted or retyped drops
  // out of `options`. Fall back to null so the picker reads as empty and the
  // required-field error fires, rather than silently showing a stale label.
  const value = options.find((o) => o.key === selectedKey) ?? null;
  return (
    <AutocompleteSingle
      label={label}
      fullWidth
      required
      value={value}
      options={options}
      disabledClearable
      error={error}
      helperText={helperText}
      // `error` reddens the outline and the label — which doubles as the
      // placeholder while nothing is picked — but the helper text explains
      // what to choose, not what went wrong, so it keeps its normal colour.
      FormHelperTextProps={{ error: false }}
      getOptionLabel={(o) => o?.label || ''}
      getOptionSelected={(o, v) => !!(o && v && o.key === v.key)}
      onChange={(next) => onChange(next || null)}
      renderOption={(o) => o?.label || ''}
      filterOptions={(os, s) => {
        const r = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && r.exec(o.label));
      }}
    />
  );
};

const CheckrPackagePicker = ({
  selectedPackageSlug,
  fallbackName,
  onChange,
  options,
  label,
  disabled,
  error,
  helperText
}: {
  selectedPackageSlug: string | undefined;
  fallbackName: string | undefined;
  onChange: (next: CheckrPackage | null) => void;
  options: CheckrPackage[];
  label?: React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}) => {
  const value: CheckrPackage | null = selectedPackageSlug
    ? options.find((o) => o.slug === selectedPackageSlug) ??
      (fallbackName
        ? { slug: selectedPackageSlug, name: fallbackName, screenings: [] }
        : null)
    : null;
  return (
    <AutocompleteSingle
      label={label}
      fullWidth
      value={value}
      options={options}
      disabled={disabled}
      disabledClearable
      error={error}
      helperText={helperText}
      getOptionLabel={(o) => o?.name || ''}
      getOptionSelected={(o, v) => !!(o && v && o.slug === v.slug)}
      onChange={(next) => onChange(next || null)}
      renderOption={(o) => o?.name || ''}
      filterOptions={(os, s) => {
        const r = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && r.exec(o.name));
      }}
    />
  );
};

const CheckrNodePicker = ({
  selectedNodeId,
  onChange,
  options,
  label
}: {
  selectedNodeId: string | undefined;
  onChange: (next: CheckrNode | null) => void;
  options: CheckrNode[];
  label?: React.ReactNode;
}) => {
  const value: CheckrNode | null = selectedNodeId
    ? options.find((o) => o.id === selectedNodeId) ?? null
    : null;
  return (
    <AutocompleteSingle
      label={label}
      fullWidth
      value={value}
      options={options}
      disabledClearable
      getOptionLabel={(o) => o?.name || ''}
      getOptionSelected={(o, v) => !!(o && v && o.id === v.id)}
      onChange={(next) => onChange(next || null)}
      renderOption={(o) => o?.name || ''}
      filterOptions={(os, s) => {
        const r = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && r.exec(o.name));
      }}
    />
  );
};

const validatePackageSelection = (
  selectedPackageSlug: string | undefined,
  selectedNodeId: string | undefined,
  packages: CheckrPackage[],
  nodes: CheckrNode[]
): string | null => {
  if (nodes.length === 0) return null;
  // No package picked yet → the form is just empty, not invalid.
  // The static `package` validator handles "Package required".
  if (!selectedPackageSlug) return null;
  const node = selectedNodeId
    ? nodes.find((n) => n.id === selectedNodeId)
    : undefined;
  if (!node) return 'subTask.backgroundCheck.invite.node.required';
  // A node with no `packageSlugs` is treated as unrestricted — any package
  // is valid, so skip the membership check.
  if (node.packageSlugs.length === 0) return null;
  const pkg = packages.find((p) => p.slug === selectedPackageSlug);
  if (pkg && !node.packageSlugs.includes(pkg.slug)) {
    return 'subTask.backgroundCheck.invite.package.notInNode';
  }
  return null;
};
